(ns frontend.react.virtualized.scrollable-grid.content-grid
  (:require
   [basic-components.field-cell-editor :as field-cell-editor]
   [basic-components.inputs.footprint :refer [editor]]
   [common.entity :as entity]
   [common.entity-utils :as eu]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.schema.calculated-field :as calculated-field]
   [common.schema.constraints :as constraints]
   [common.schema.editor-types :as editor-types]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.option-filters :as filters]
   [common.schema.registry :as registry]
   [frontend.actions.entity :as entity-actions]
   [frontend.bootstrap.components :as rbs]
   [frontend.intelligence.icon :as intelligence-icon]
   [frontend.logging :as logging]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.events :as events]
   [frontend.react.virtualized.scrollable-grid.selection-grid :as selection-grid]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as state-permissions]
   [frontend.utils :as fu]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.dynamic.view :as dynamic-modal]
   [frontend.view.modals.events :as modals]
   [reagent.core :as r]))

(defn show-tooltip?
  [columns column-key]
  (let [field-schema (:field-schema (get columns column-key))
        grid-type (-> field-schema
                      :field/default-visibility
                      :grid)]
    (editor-types/text-like-editor-type? grid-type)))

;; if integer is too large, it turns into goog.math.Long => conversion to string is necessary.
(defn coerce
  [cell-value base-multiplier]
  (cond
    (instance? js/goog.math.Long cell-value) (str cell-value)
    (fu/is-bigdec? cell-value) (fu/format-number-with-mult
                                (fu/bigdec->float cell-value)
                                (or base-multiplier
                                    (some->> @session/tenant
                                             :tenant/cur-multiplier)))
    (number? cell-value) (str cell-value)
    (boolean? cell-value) (if cell-value
                            (loc/tr :true)
                            (loc/tr :false))
    :else cell-value))

;; TODO: this is copied from entity-editor, please make a generic function of this
(defn resolve-template
  [{:keys [entity tenant-schema remove-fields pipeline-id]}]
  (let [entity-type (:entity/type entity)
        entity-schema (registry/get-schema tenant-schema entity-type)]
    (->>
     (case entity-type
       :entity.type/fund-commitment (dissoc entity-schema :fund-commitment/overview)
       :entity.type/fundraising-opportunity (dissoc entity-schema
                                                    :fundraising-opportunity/description)
       :entity.type/person (apply dissoc entity-schema remove-fields)
       :entity.type/oppo (apply dissoc
                                entity-schema
                                (conj remove-fields
                                      (when-not (= (:oppo/status entity) :oppo.status/on-hold)
                                        :oppo/on-hold-until)))                                      ;; show only when on-hold
       :entity.type/company (apply dissoc entity-schema remove-fields)
       (apply dissoc entity-schema remove-fields))
     (remove (fn [[k _v]]
               (or (some #{k} remove-fields)
                   (not (state-permissions/field-read-access? k entity-type pipeline-id)))))
     (keep
      (fn [[attribute field-schema]]
        (let [editor-type (->> field-schema
                               :field/default-visibility
                               :grid)
              field-custom-params (:field/custom-params field-schema)]
          (when-not (= editor-type :editor.type/none)
            (merge
             field-schema
             {:attribute   attribute
              :entity-id   (:db/id entity)
              :access/type (state-permissions/access-to-field-long
                            attribute
                            (:entity/type entity)
                            (fu/pipeline-id entity))
              :editor/type editor-type}
             (when (fn? field-custom-params)
               (field-custom-params entity))))))))))

(defn field-cell-editor-schema
  [{:keys [attribute field-schema ref-atom hover-atom entity-id
           pipeline-id change-status! status-key reset-focus!
           row-index row-count status-options]}]
  (let [entity-atom (r/cursor session/entities [entity-id])
        entity @entity-atom
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        entity-type (:entity/type entity)
        enriched-entity (ic/enrich-entity entity)
        entity-template (resolve-template {:entity        @entity-atom
                                           :tenant-schema tenant-schema
                                           :account       @session/account
                                           :role          @session/role
                                           :remove-fields []
                                           :pipeline-id   pipeline-id})
        entity-template-lookup (->> entity-template
                                    (map (fn [field-schema]
                                           [(:attribute field-schema) field-schema]))
                                    (into {}))
        value (fu/get-value tenant tenant-schema entity attribute)
        intelligence-value (get enriched-entity attribute)
        is-intelligence-value? (and (nil? value)
                                    (contains? enriched-entity attribute))
        no-value? (and (fu/empty-value? value)
                       (fu/empty-value? intelligence-value))
        related-fields (some->> field-schema
                                :field/related-fields)
        status-attribute? (= attribute status-key)
        index-from-end (- row-count row-index)
        select-menu-placement (if (and (>= row-index 5)                                             ;; open first 5 rows down
                                       (<= index-from-end 5))
                                "top"
                                "auto")
        datepicker-position (if (and (>= row-index 5)
                                     (< 5 index-from-end 10))
                              "top left"
                              "bottom left")
        max-opts-visible (when (and (>= row-index 5)
                                    (< 5 index-from-end 10))
                           5)
        editor-type (->> field-schema
                         :field/default-visibility
                         :grid)]
    (merge
     field-schema
     {:menu-placement        select-menu-placement
      :position              datepicker-position
      :max-opts-visible      max-opts-visible
      :close-menu-on-scroll? true
      :menu-portal-target    (.getElementById js/document "react-select-menu-container")
      :value                 (if is-intelligence-value? intelligence-value value)
      :containing-entity     entity
      :class-name            (fu/cs (:class-name field-schema)
                                    (when no-value? "empty"))
      :open-on-start?        true                                                                   ;; date picker and select
      :on-open               (fn []
                               (swap! hover-atom assoc :field-editor-opened? true))
      :on-close              (fn []
                               (when (:field-editor-opened? @hover-atom)
                                 (swap! hover-atom dissoc :field-editor-opened?))
                               (reset-focus!))
      :on-blur!              reset-focus!
      :editor/type           editor-type
      :entity-types-atom     session/entity-types
      :entity-type           entity-type
      :entity-unique-name    (entity/unique-name-or-id entity)
      ;; TODO: take language from user / tenant config -> default to :en
      ;; if only :en in field/translations use :en
      :label                 (or (->> field-schema
                                      :field/translations
                                      :en)
                                 (loc/field-name attribute field-schema)
                                 (loc/tr attribute))                                                    ;; we select label outside of field-editor so we can customize label if needed
      ;:field-access [:x :w :r] ;; TODO: Fairly urgent - change the way field editors access is resolved everywhere so that the access is passed from outside
      :disabled?             (->> (if (map? related-fields)
                                    related-fields
                                    {related-fields nil})
                                  (some (fn [[related-attribute restriction]]
                                          (let [related-value (get entity related-attribute)]
                                            (case restriction
                                              :required-value (when-not related-value :needed-value)
                                              nil)))))
      :ref-atom              ref-atom
      :auto-focus?           true
      :on-menu-open!         (fn []
                               (swap! hover-atom assoc :field-editor-opened? true))
      :on-menu-close!        (fn []
                               (when (:field-editor-opened? @hover-atom)
                                 (swap! hover-atom dissoc :field-editor-opened?)))
      :placeholder           ""
      :validate!             (fn [attribute value]
                               (let [schema (get entity-template-lookup attribute)
                                     field-constraints (:field/constraints schema)
                                     errors (constraints/validate
                                             field-constraints
                                             (:db/id entity)
                                             value
                                             entity
                                             (partial session/unique-check false (partial routing/open-entity))
                                             loc/tr
                                             logging/warn)]
                                 (swap! hover-atom assoc :error errors)
                                 errors))
      :error-text            nil                                                                    ;; TODO: use editor-atom to store validation and save errors
      :on-cancel-edit!       (fn [_]
                               (grid/clear-focus! hover-atom))
      :on-save!              (fn [attribute new-value on-success! on-error!]
                               (let [value (fu/get-value tenant tenant-schema entity attribute)
                                     retract-optimistic!
                                     (fn []
                                       (when (= new-value (get @entity-atom attribute))
                                         (swap! entity-atom assoc attribute value)
                                         (deref entity-atom)))]
                                 ;;make sure local state is cleared when no changes:
                                 (cond
                                   status-attribute?
                                   (do
                                     ;; clear focus before opening dialogs -> dialog close will reset focus
                                     (grid/clear-focus! hover-atom)
                                     (change-status! {:entity       entity
                                                      :new-value    new-value
                                                      :reset-focus! reset-focus!}))

                                   ;; Stage column change: route through the entry-criteria flow
                                   ;; (no-target picker + criteria modal). Optimistic update fires
                                   ;; only when the save actually fires (via :before-save!) so a
                                   ;; criteria-modal cancel leaves the cell on its old stage.
                                   (pipeline/stage-attribute? attribute)
                                   (do
                                     (grid/clear-focus! hover-atom)
                                     (f-entity/attempt-move-to-stage!
                                      {:opportunity entity
                                       :target-stage new-value
                                       :before-save! (fn []
                                                       (swap! entity-atom assoc attribute new-value)
                                                       (deref entity-atom))
                                       :on-success! (fn [_]
                                                      (when (fn? on-success!)
                                                        (on-success!)))
                                       :on-error! (fn [err]
                                                    (retract-optimistic!)
                                                    (when (fn? on-error!)
                                                      (on-error! err))
                                                    (.focus (:grid-ref @ref-atom)))}))
                                   :else
                                   (if (or (and (= new-value value)
                                                (fn? on-success!))
                                           (nil? new-value))
                                     (reset-focus!)
                                     (when-not (= new-value value)
                                       (if-let [create-value (:new-option new-value)]
                                         (events/update-with-new-entity!
                                          {:enriched-entity enriched-entity
                                           :attribute       attribute
                                           :value           value
                                           :create-value    create-value
                                           :on-success!     (fn [_]
                                                              (reset-focus!)
                                                              ;; TODO: force grid rerender !!!!
                                                              (when on-success! (on-success!)))
                                           :on-error!       (fn [err]
                                                              (when on-error!
                                                                (on-error! err))
                                                              (.focus (:grid-ref @ref-atom)))})
                                         (do
                                           ;;optimistic save to frontend db:
                                           (swap! entity-atom assoc attribute new-value)
                                           (deref entity-atom)
                                           (events/update-entity!
                                            {:enriched-entity enriched-entity
                                             :attribute       attribute
                                             :new-value       new-value
                                             :on-success!     (fn [_]
                                                                (when-not (some #{editor-type}
                                                                                [:editor.type/date
                                                                                 :editor.type/datetime])
                                                                  (reset-focus!)
                                                                  (when on-success! (on-success!))))
                                             :on-error!       (fn [err]
                                                                ;;retract the optimistic save above
                                                                (when (= new-value
                                                                         (get @entity-atom attribute))
                                                                  (swap! entity-atom assoc attribute value)
                                                                  (deref entity-atom))
                                                                (on-error! err)
                                                                (.focus (:grid-ref @ref-atom)))}))))))))}
     (when is-intelligence-value?                                                                   ;; we decide source outside
       {:source-options {:source-icon-fn intelligence-icon/view}})
     (when-let [secondary-attribute (:field/secondary-attribute field-schema)]                      ;; secondary attribute
       {:secondary-view-args
        (let [sec-templ (get entity-template-lookup secondary-attribute)
              secondary-field-schema (registry/get-field-schema tenant-schema secondary-attribute)]
          (merge
           sec-templ
           ;; TODO: fix composite-attribute to check calculated field instead
           {:value      (if-let [_composite-attributes (:field/composite-attributes
                                                       secondary-field-schema)]
                          (let [calc-function (:field/calculated-field secondary-field-schema)]
                            (when (fn? calc-function)
                              (calculated-field/calculate
                               {:calc-fn          calc-function
                                :entity-data      entity
                                :entity-lookup    session/get-entity
                                :reverse-lookup   session/reverse-lookup
                                :tr               loc/tr
                                :bigdec->float    fu/bigdec->float
                                :resolve-value    (partial eu/get-value tenant tenant-schema)
                                :float->bigdec    fu/float->bigdec
                                :get-field-schema (partial registry/get-field-schema tenant-schema)
                                :display-string   entity/display-string})))
                          (or (get @entity-atom secondary-attribute)
                              (when (= (:field/data-type sec-templ) :data.type/currency-symbol)
                                (:tenant/default-currency @session/tenant))))
            :label      (loc/field-name secondary-attribute secondary-field-schema)                 ;; when we have custom dynamic fields the labels come from tenant configuration
            :mandatory? false                                                                       ;;TODO: fix this - also in entity-editor mandatory-field?
            :error-text nil}))})
     (when (and status-attribute? status-options)
       {:options (status-options entity)}))))

(defn open-editor-modal!
  [{:keys [attribute hover-atom entity-id
           editor-type value field-schema reset-focus!]}]
  ;; clear focus before opening dialogs -> dialog close will reset focus
  (grid/clear-focus! hover-atom)

  (modals/open-modal!
   dynamic-modal/modal-key
   {:title               (loc/field-name attribute field-schema)
    :on-close!           reset-focus!
    :show-button?        true
    :button-text         (loc/tr :save-changes)
    :custom-validator-fn (constantly nil)                                                           ;; todo validate using the constraints for this field f.e min width mandatory field ...
    :on-submit!          (fn [state]
                           ;; TODO update field only if new value differs from old value. value passed to modal is not necessarily up-to-date anymore at on-submit time.
                           (if (nil? (:value @state))
                             (reset-focus!)
                             (entity-actions/*->field-update!
                              entity-id
                              attribute
                              (:value @state)
                              (fn [_res]
                                (reset-focus!))
                              (fn [err]
                                (grid/clear-focus! hover-atom)
                                (logging/error "Error " err)))))
    :render-fn           (fn [modal-state _close-modal!]
                           [:div.mb-3
                            [editor
                             (merge
                              field-schema
                              {:editor/type           editor-type
                               :state-atom            modal-state
                               :close-menu-on-scroll? true
                               :menu-portal-target    (.getElementById js/document
                                                                       "react-select-menu-container")
                               :placeholder           (loc/field-name attribute field-schema)
                               ;; TODO updates to value won't cause automatic rerender. Replace with ratom/reaction/cursor.
                               :value                 (if (:value @modal-state)
                                                        (:value @modal-state)
                                                        value)
                               :on-change!            (fn [_e new-value]
                                                        (swap! modal-state assoc :value new-value))
                               :auto-focus            true
                               :cursor-at-end?        true})]])}))

(defn edit-allowed-view
  [{:keys [entity attribute field-schema editor-type row-data hover-atom row-count row-index
           ref-atom pipeline-key owner-key status-options]}]
  (let [entity-id (:db/id row-data)
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        value (fu/get-value tenant tenant-schema entity attribute)
        entity (session/get-entity entity-id)
        owner (get entity owner-key)
        pipeline-id (get entity pipeline-key)
        active-accounts (->> (session/active-accounts pipeline-id)
                             (remove (fn [a]
                                       (= (:db/id a) owner))))
        field-schema
        (merge field-schema
               (when (and active-accounts
                          (= attribute :common/team-members))
                 {:options                active-accounts
                  :field/option-filter-fn (fn [opts]
                                            (let [entity-candidate-id (-> opts
                                                                          :entity-candidate
                                                                          :lower-case-entity
                                                                          :db/id)
                                                  is-not-owner (not= entity-candidate-id owner)]
                                              (and is-not-owner (filters/active-users opts))))}))
        open-modal? (editor-types/modal-editor-type? editor-type)
        boolean-data-type? (= :data.type/boolean (:field/data-type field-schema))
        ;; we must store the focus as the clear focus will lose the last focused cell
        focused-row (:focused-cell-row @hover-atom)
        focused-col (:focused-cell-col @hover-atom)
        reset-focus! (fn []
                       (grid/make-cell-active {:row   focused-row
                                               :col   focused-col
                                               :state hover-atom})
                       (when-not boolean-data-type?
                         (grid/clear-focus! hover-atom))
                       (.focus (:grid-ref @ref-atom)))]
    (if open-modal?
      (do
        (open-editor-modal! {:attribute    attribute
                             :hover-atom   hover-atom
                             :entity-id    entity-id
                             :editor-type  editor-type
                             :value        value
                             :field-schema field-schema
                             :reset-focus! reset-focus!})
        (fn [{:keys [cell-value]}]
          [:div
           {:class "mb-0 text-sm truncate "}
           cell-value]))

      (fn [{:keys [attribute field-schema hover-atom
                   ref-atom pipeline-id change-status! status-key]}]
        (let [entity-atom (r/cursor session/entities [entity-id])
              entity @entity-atom
              entity-type (:entity/type entity)
              has-access? (state-permissions/field-read-access? attribute entity-type pipeline-id)
              render? (and has-access?
                           (not= (->> field-schema
                                      :field/default-visibility
                                      :grid)
                                 :editor.type/none))]
          [:div.zap-grid__field-editor-wrapper
           (when render?
             ^{:key attribute}
             [field-cell-editor/view
              (field-cell-editor-schema {:attribute      attribute
                                         :field-schema   field-schema
                                         :ref-atom       ref-atom
                                         :hover-atom     hover-atom
                                         :entity-id      entity-id
                                         :pipeline-id    pipeline-id
                                         :change-status! change-status!
                                         :status-key     status-key
                                         :reset-focus!   reset-focus!
                                         :row-index      row-index
                                         :row-count      row-count
                                         :status-options status-options})])
           (when (:error @hover-atom)
             [:div.zap-grid__field-cell-error (:error @hover-atom)])])))))

(defn read-only-cell
  [cell-value last-cell?]
  [:div.zap-grid__field-editor-wrapper
   [:span
    {:class "block mb-0 text-sm"}
    cell-value]
   [:div
    {:class-name (fu/cs (if last-cell?
                          "zap-grid__field-cell-read-only-last"
                          "zap-grid__field-cell-read-only"))}
    (loc/tr :grid-cell-read-only)]])

(defn cell-editor
  [{:keys [hover-atom row-data columns col-index row-index row-count
           cell-value read-only-field? ref-atom pipeline-key pipeline-id
           field-schema owner-key change-status! status-key status-options]}]
  (let [editor-type (->> field-schema
                         :field/default-visibility
                         :grid)
        attribute (:attribute field-schema)
        entity-id (:db/id row-data)
        entity (session/get-entity entity-id)
        raw-field-value (get entity attribute)
        last-cell? (= (dec row-count) (:focused-cell-row @hover-atom))]
    (cond
      (and (= editor-type :editor.type/link)
           raw-field-value)
      (do (routing/open-entity raw-field-value)
          [:div
           [:i.fal.fa-spinner.fa-pulse.fa-3x]])
      (or read-only-field?
          (= editor-type :editor.type/link)
          (grid/ref-field? {:columns columns
                            :col     col-index}))
      [read-only-cell cell-value last-cell?]
      :else
      [edit-allowed-view
       {:entity         entity
        :row-count      row-count
        :row-index      row-index
        :attribute      attribute
        :field-schema   field-schema
        :editor-type    editor-type
        :row-data       row-data
        :hover-atom     hover-atom
        :ref-atom       ref-atom
        :pipeline-id    pipeline-id
        :pipeline-key   pipeline-key
        :owner-key      owner-key
        :change-status! change-status!
        :status-key     status-key
        :cell-value     cell-value
        :status-options status-options}])))

(defn cell-view-mode
  [{:keys [resizing-column columns col-index row-data data-key cell-value
           on-row-click! row-index name-field? render-as-link?]}]
  (let [tooltip-text (when (= data-key :task/message)
                       (loc/tr :click-to-open))
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        tooltip-text (or tooltip-text
                         (when-let [tt-getter (:tooltip-text-getter row-data)]
                           (tt-getter data-key))
                         (str cell-value))
        data-key (:data-key (get columns col-index))]
    (fu/overlay-if
     (and (not resizing-column)
          (grid/show-tooltip? columns col-index))
     [rbs/OverlayTrigger
      {:placement     "right"
       :popper-config {:modifiers [{:name    "preventOverflow"
                                    :options {:main-axis false}}
                                   {:name "hide"}]}
       :overlay       (r/as-element [rbs/Tooltip
                                     {:class "in"
                                      :id    (str key "-tooltip")}
                                     tooltip-text])}]
     [:div
      {:class "w-full flex items-center"}
      [:span
       {:class-name (fu/cs "block mb-0 text-sm truncate"
                           (when (keyword? data-key)
                             data-key)
                           (when (and (coll? data-key) (seq data-key))
                             (first data-key)))}
       (cond
         name-field?
         [:a.read-only__name-field
          {:on-click
           (fn [_]
             (when (fn? on-row-click!)
               (on-row-click! row-index)))}
          (fu/get-value tenant tenant-schema row-data data-key)]
         (and render-as-link?
              (not= data-key :screening-profile/uuid))                                              ; Todo - quick hack fix this
         [editor
          {:editor/type :editor.type/link
           :entity-id   (fu/get-value tenant tenant-schema row-data data-key)}]
         :else
         cell-value)]])))

(defn cell-render
  [props js-args]
  (let [{:keys [columns row-data-getter on-cell-click hover-atom hover-col
                hover-row resizing-column editable-grid? on-row-click! row-count
                ref-atom link-to-entity-field read-only-fields pipeline-key
                owner-key change-status! status-key status-options]}
        props
        {:keys [columnIndex rowIndex isScrolling isVisible key style] :as _args}
        (js->clj js-args :keywordize-keys true)
        select-cell? (= columnIndex 0)
        data-column-index (- columnIndex 1)
        row-data (row-data-getter rowIndex)
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        data-key (-> columns
                     (get data-column-index)
                     :data-key)
        ;; TODO data conversion should be done here not in row-data-getter, we end up doing it too many times
        field-schema (registry/get-field-schema tenant-schema data-key)
        cell-value (if-let [display-cell (get-in row-data [:display-data data-key])]
                     display-cell
                     (fu/display-data tenant-schema tenant data-key field-schema row-data)          ;; need to recalculate here
                     )
        coerced-cell-value (coerce cell-value
                                   (when-let [custom (:field/custom-params field-schema)]
                                     (when (fn? custom)
                                       (:base-multiplier (custom nil)))))
        ;; TODO data conversion should be done here not in row-data-getter, we end up doing it too many times
        {:keys [active-cell-row active-cell-col focused-cell-col focused-cell-row]} @hover-atom
        active-cell? (and (= data-column-index active-cell-col)
                          (= rowIndex active-cell-row))
        focused-cell? (and (= data-column-index focused-cell-col)
                           (= rowIndex focused-cell-row))
        entity-atom (r/cursor session/entities [(:db/id row-data)])
        entity @entity-atom
        pipeline-id (fu/pipeline-id entity)
        attribute (:attribute field-schema)
        read-only-field? (or (grid/read-only-access? {:attribute   attribute
                                                      :entity      entity
                                                      :pipeline-id pipeline-id
                                                      :data-key    data-key})
                             (grid/read-only-field? data-key read-only-fields))
        name-field? (= link-to-entity-field data-key)
        editor-type (->> field-schema
                         :field/default-visibility
                         :grid)
        cell-class-name (fu/cs "px-2 flex items-center bg-base-light border-b-2 border-s border-r border-solid border-base-light-border-paler "
                               (condp = (:field/data-type field-schema)
                                 :data.type/number "number-align"
                                 :data.type/multiplier "number-align"
                                 :data.type/percentage "number-align"
                                 :data.type/currency-value "number-align"
                                 :data.type/integer "number-align"
                                 "")
                               (when (= columnIndex hover-col) "hover-col")
                               (when (= rowIndex hover-row) "hover-row")
                               (when (and active-cell?
                                          (not focused-cell?))
                                 "active-cell")
                               (when (and (or active-cell?
                                              focused-cell?)
                                          (or read-only-field?
                                              (= editor-type :editor.type/link)
                                              (grid/ref-field? {:columns columns
                                                                :col     columnIndex})))
                                 "read-only-cell")
                               (when (and focused-cell?
                                          (not read-only-field?))
                                 "focused-cell")
                               (when on-cell-click "clickable")
                               (when (and (not resizing-column)
                                          (-> @hover-atom
                                              :drag
                                              :column
                                              (= data-key)))
                                 "drag-over-column")
                               (if (odd? rowIndex) "odd" "even"))]
    (cond
      (not isVisible)
      ""
      isScrolling
      (if select-cell?
        (selection-grid/render-cell props rowIndex style key)
        (r/as-element
         [:div
          {:key        key
           :style      style
           :class-name (fu/cs "px-2 flex items-center bg-base-light border border-solid border-base-light-border-paler "
                              (if (odd? rowIndex) "odd" "even"))}
          [:span
           {:class "block mb-0 text-sm truncate"}
           coerced-cell-value]]))
      :else
      (let [;;TODO hardcoded special case for now; if necessary, figure out more generic way
            cell-hiccup (if select-cell?
                          (selection-grid/render-cell props rowIndex style key)
                          (r/as-element
                           [:div
                            (merge
                             {:key           key
                              :style         style
                              :on-mouse-over (fn [_]
                                               (swap! hover-atom assoc
                                                      :hover
                                                      {:col columnIndex
                                                       :row rowIndex}))
                              :class-name    cell-class-name}
                             {:on-double-click (fn [_]
                                                 (grid/add-focus! {:state hover-atom}))
                              :on-click        (fn [e]
                                                 (if editable-grid?
                                                   (when (fn? on-cell-click)
                                                     (on-cell-click rowIndex data-column-index e))
                                                   (when (fn? on-row-click!)
                                                     (on-row-click! rowIndex e))))})
                            (cond
                              (and focused-cell?
                                   name-field?)
                              (do (routing/open-entity (:db/id row-data))
                                  [:div
                                   [:i.fal.fa-spinner.fa-pulse.fa-3x]])
                              focused-cell?
                              [cell-editor
                               {:hover-atom       hover-atom
                                :row-data         row-data
                                :columns          columns
                                :row-index        rowIndex
                                :row-count        row-count
                                :col-index        data-column-index
                                :cell-value       coerced-cell-value
                                :read-only-field? read-only-field?
                                :pipeline-key     pipeline-key
                                :pipeline-id      pipeline-id
                                :ref-atom         ref-atom
                                :field-schema     field-schema
                                :owner-key        owner-key
                                :change-status!   change-status!
                                :status-key       status-key
                                :status-options   status-options}]
                              :else
                              [cell-view-mode
                               {:resizing-column resizing-column
                                :columns         columns
                                :col-index       data-column-index
                                :data-key        data-key
                                :row-data        row-data
                                :cell-value      coerced-cell-value
                                :row-index       rowIndex
                                :on-row-click!   on-row-click!
                                :render-as-link? (= :editor.type/link
                                                    (->> field-schema
                                                         :field/default-visibility
                                                         :grid))
                                :name-field?     name-field?}])]))]
        cell-hiccup))))

(defn view
  [_args]
  (fn [args]
    (let [{:keys [ref-atom row-height col-count row-count avail-width avail-height overscan-count
                  hover-atom column-width-fn no-rows-renderer grid-store scroll-left
                  scroll-top on-scroll]}
          args
          {:keys [active-cell-col active-cell-row]} @hover-atom]
      [:div.zap-grid__field-cell-editor-wrapper
       {:tab-index   0
        :ref         (fn [el]
                       (when-not (:grid-ref @ref-atom)
                         (swap! ref-atom assoc :grid-ref el)))
        :on-key-down (r/partial grid/handle-cell-key-down
                                {:state     hover-atom
                                 :ref-atom  ref-atom
                                 :row-count row-count
                                 :col-count col-count})}
       (r/as-element
        (into
         [:> js/ReactVirtualized.Grid
          (merge
           {:class-name            (fu/cs "zap-grid__content")
            :cell-renderer         (r/partial cell-render args)
            :column-width          (r/partial column-width-fn)
            :column-count          (+ col-count 1)
            :width                 avail-width
            :height                avail-height
            :overscan-column-count overscan-count
            :row-height            (or row-height 40)
            :overscan-row-count    overscan-count
            :on-scroll             on-scroll
            :no-content-renderer   no-rows-renderer
            :row-count             row-count
            :ref                   (fn [component]
                                     ;; save the grid (react) component, so it can be referenced elsewhere
                                     ;; TODO: can this be done at creation of js/ReactVirtualized.Grid instead of here?
                                     (when (and component
                                                (not (= component (:content-grid @grid-store))))
                                       (swap! grid-store assoc :content-grid component)))}
           (when scroll-left
             {:scroll-left scroll-left})
           (when scroll-top
             {:scroll-top scroll-top})
           (when active-cell-col
             {:scroll-to-column (inc active-cell-col)})
           (when active-cell-row
             {:scroll-to-row active-cell-row}))]))])))
